import type { RawComponent, RawQuestionSettings } from './editorTypes';

export type QuestionAssignmentType = 'ALL' | 'FIXED' | 'RANDOM' | 'USER_DEFINED';

export interface QuestionDisplayItem<TQuestion extends { id: string }> {
  arrayIndex: number;
  assignmentPosition?: number;
  canDrag: boolean;
  displayLabel: string;
  displayNumber: number;
  isAssigned: boolean;
  isPractice: boolean;
  question: TQuestion;
}

export type QuestionReorderAction =
  | {
      fromPosition: number;
      toPosition: number;
      type: 'assignment';
    }
  | {
      fromIndex: number;
      toIndex: number;
      type: 'questions';
    };

const ASSIGNMENT_TYPE_MAP: Record<string, QuestionAssignmentType> = {
  '0': 'ALL',
  '1': 'ALL',
  '2': 'FIXED',
  '3': 'RANDOM',
  '4': 'USER_DEFINED',
  ALL: 'ALL',
  FIXED: 'FIXED',
  RANDOM: 'RANDOM',
  UNKNOWN: 'ALL',
  USER_DEFINED: 'USER_DEFINED',
};

function normalizeString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function getQuestionAssignmentType(
  settings: RawQuestionSettings | null | undefined,
): QuestionAssignmentType {
  if (typeof settings?.type === 'number') {
    return ASSIGNMENT_TYPE_MAP[String(settings.type)] ?? 'ALL';
  }

  const normalized = normalizeString(settings?.type)?.toUpperCase();
  return normalized ? (ASSIGNMENT_TYPE_MAP[normalized] ?? 'ALL') : 'ALL';
}

export function getUserDefinedNumbers(
  questionCount: number,
  settings: RawQuestionSettings | null | undefined,
) {
  const rawNumbers = settings?.user_defined_numbers ?? settings?.userDefinedNumbers;

  if (!Array.isArray(rawNumbers)) {
    return [];
  }

  return Array.from(
    new Set(
      rawNumbers
        .map((entry) => normalizeNumber(entry))
        .filter((entry): entry is number => entry !== undefined)
        .map((entry) => Math.trunc(entry))
        .filter((entry) => entry >= 0 && entry < questionCount),
    ),
  );
}

/**
 * The assignment settings that govern how a question list is numbered.
 *
 * Shared so the section editor and the chapter page cannot resolve them
 * differently and end up numbering the same question two ways.
 */
export function getQuestionSettingsForEditor(
  component: RawComponent | undefined,
  kind: 'homework' | 'preLecture',
): RawQuestionSettings | undefined {
  if (!component) {
    return undefined;
  }

  const settings =
    kind === 'preLecture'
      ? component.pre_lecture_question_settings ??
        component.preLectureQuestionSettings
      : component.question_settings ??
        component.questionSettings ??
        component.course_question_settings ??
        component.courseQuestionSettings;

  return settings && typeof settings === 'object'
    ? (settings as RawQuestionSettings)
    : undefined;
}

export function buildQuestionDisplayList<TQuestion extends { id: string }>(
  questions: TQuestion[],
  settings: RawQuestionSettings | null | undefined,
): Array<QuestionDisplayItem<TQuestion>> {
  const assignmentType = getQuestionAssignmentType(settings);

  if (assignmentType !== 'USER_DEFINED') {
    return questions.map((question, arrayIndex) => ({
      arrayIndex,
      canDrag: true,
      displayLabel: `Question ${arrayIndex + 1}`,
      displayNumber: arrayIndex + 1,
      isAssigned: true,
      isPractice: false,
      question,
    }));
  }

  const assignedIndices = getUserDefinedNumbers(questions.length, settings);
  const assignedSet = new Set(assignedIndices);
  const assignedItems = assignedIndices.map((arrayIndex, assignmentPosition) => ({
    arrayIndex,
    assignmentPosition,
    canDrag: true,
    displayLabel: `Question ${assignmentPosition + 1}`,
    displayNumber: assignmentPosition + 1,
    isAssigned: true,
    isPractice: false,
    question: questions[arrayIndex]!,
  }));
  const practiceItems = questions
    .map((question, arrayIndex) => ({
      arrayIndex,
      question,
    }))
    .filter(({ arrayIndex }) => !assignedSet.has(arrayIndex))
    .map(({ arrayIndex, question }, practicePosition) => ({
      arrayIndex,
      canDrag: false,
      displayLabel: `Practice Question ${practicePosition + 1}`,
      displayNumber: practicePosition + 1,
      isAssigned: false,
      isPractice: true,
      question,
    }));

  return [...assignedItems, ...practiceItems];
}

export function reorderAssignment(
  currentAssignedIndices: number[],
  fromPosition: number,
  toPosition: number,
): number[] {
  if (
    fromPosition === toPosition ||
    fromPosition < 0 ||
    toPosition < 0 ||
    fromPosition >= currentAssignedIndices.length ||
    toPosition >= currentAssignedIndices.length
  ) {
    return currentAssignedIndices;
  }

  const nextAssignedIndices = [...currentAssignedIndices];
  const [movedIndex] = nextAssignedIndices.splice(fromPosition, 1);

  if (movedIndex === undefined) {
    return currentAssignedIndices;
  }

  nextAssignedIndices.splice(toPosition, 0, movedIndex);
  return nextAssignedIndices;
}

export function getQuestionReorderAction<TQuestion extends { id: string }>(
  displayItems: Array<QuestionDisplayItem<TQuestion>>,
  assignmentType: QuestionAssignmentType,
  activeId: string,
  overId: string | null,
): QuestionReorderAction | null {
  if (!overId || activeId === overId) {
    return null;
  }

  const activeItem = displayItems.find((item) => item.question.id === activeId);
  const overItem = displayItems.find((item) => item.question.id === overId);

  if (
    !activeItem ||
    !overItem ||
    !activeItem.canDrag ||
    !overItem.canDrag ||
    activeItem.isAssigned !== overItem.isAssigned
  ) {
    return null;
  }

  if (assignmentType === 'USER_DEFINED') {
    if (
      activeItem.assignmentPosition === undefined ||
      overItem.assignmentPosition === undefined
    ) {
      return null;
    }

    return {
      fromPosition: activeItem.assignmentPosition,
      toPosition: overItem.assignmentPosition,
      type: 'assignment',
    };
  }

  return {
    fromIndex: activeItem.arrayIndex,
    toIndex: overItem.arrayIndex,
    type: 'questions',
  };
}

export function remapAssignedIndicesAfterDelete(
  oldIndices: number[],
  deletedIndex: number,
): number[] {
  return oldIndices
    .filter((index) => index !== deletedIndex)
    .map((index) => (index > deletedIndex ? index - 1 : index));
}
